import { useRef, useState } from 'react';
import { FileText, Trash2, Upload } from 'lucide-react';
import {
  uploadAttachment,
  deleteAttachment,
  planApiErrorMessage,
} from '@/utils/planApi';
import { api } from '@/utils/api';
import type { PlanAttachment, PlanDataV2 } from '@/types/planData';

type AttachmentType = PlanAttachment['type'];

// ---------------------------------------------------------------------------
// AttachmentSlot (blueprint v2 F-5) — supporting-document uploads on a
// construction plan: crane company lift plan (PDF/images, §8.3), structural /
// mount analyses (§8.7 pattern), QE report, other. Proof-test photos have
// their own C-4b log (ProofTestLog.tsx) on the same B-2 multer route with
// type='proof-test' and are NOT surfaced here to avoid double-management.
//
// Everything rides the EXISTING B-2 attachment infra (blueprint ground rule):
// POST /api/construction-plans/:id/attachments (multer, 20MB, §8.3 metadata
// JSON on the 'metadata' form field) and DELETE .../attachments/:name. No
// parallel upload system — planApi.uploadAttachment / planApi.deleteAttachment.
// The §8.7 UI (capacity-comparison engine) is explicitly post-v1.1; only the
// attachment `type` values land now.
// ---------------------------------------------------------------------------

/** §8.7 doc types selectable in this slot (proof-test is C-4b-only). */
export const ATTACHMENT_SLOT_TYPES = [
  'craneLiftPlan',
  'structuralAnalysis',
  'mountAnalysis',
  'qeReport',
  'other',
] as const satisfies Exclude<AttachmentType, 'proof-test'>[];

export function attachmentTypeLabel(type: AttachmentType): string {
  switch (type) {
    case 'craneLiftPlan':
      return 'Crane Company Lift Plan';
    case 'structuralAnalysis':
      return 'Structural Analysis';
    case 'mountAnalysis':
      return 'Mount Analysis';
    case 'qeReport':
      return 'QE Report';
    case 'proof-test':
      return 'Proof-Test Photo';
    default:
      return 'Other';
  }
}

/** PDFs + images only (matches the B-2 blueprint spec: "PDF/images"). */
export function acceptsFile(file: File): boolean {
  return (
    file.type === 'application/pdf' ||
    file.type.startsWith('image/')
  );
}

/** Human-readable byte size for the file row. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * F-5 audit round 1 (MEDIUM): proof-test photos are managed exclusively by
 * the C-4b ProofTestLog — an entry of type='proof-test' that reaches this
 * slot (e.g. via the page's server-mirror) must never render a row with a
 * working Delete, because deleting it here orphans the ProofTestLog state
 * (the server delete route has no type check). The invariant lives right
 * under the exclusion comment above so the two can't drift.
 */
export function isSlotManaged(a: PlanAttachment): boolean {
  return a.type !== 'proof-test';
}

const METADATA_KEYS = [
  'docNumber',
  'company',
  'operator',
  'craneModel',
  'tonnage',
  'planDate',
  'revision',
] as const;

/**
 * Validate the §8.3 metadata form before it rides the 'metadata' JSON field.
 * docNumber is required (audit traceability); everything else optional; unknown
 * keys rejected so the server-side shape stays closed.
 */
export function validateAttachmentMetadata(
  meta: Record<string, unknown>
): { ok: boolean; cleaned?: PlanDataV2['attachments'][number]['metadata'] } {
  if (typeof meta !== 'object' || meta === null || Array.isArray(meta)) return { ok: false };
  const keys = Object.keys(meta);
  if (keys.some((k) => !(METADATA_KEYS as readonly string[]).includes(k))) return { ok: false };
  const docNumber = meta.docNumber;
  if (typeof docNumber !== 'string' || !docNumber.trim()) return { ok: false };
  const cleaned: Record<string, string> = { docNumber: docNumber.trim() };
  for (const key of METADATA_KEYS) {
    if (key === 'docNumber') continue;
    const value = meta[key];
    if (value === undefined || value === null || value === '') continue;
    if (typeof value !== 'string') return { ok: false };
    cleaned[key] = value.trim();
  }
  return { ok: true, cleaned: cleaned as PlanDataV2['attachments'][number]['metadata'] };
}

export interface AttachmentSlotProps {
  attachments: PlanAttachment[];
  onChange: (next: PlanAttachment[]) => void;
  /** Saved plan id — uploads go through the B-2 route and need it. */
  planId: number | null;
  canEdit: boolean;
  onError?: (message: string) => void;
}

export function AttachmentSlot({
  attachments,
  onChange,
  planId,
  canEdit,
  onError,
}: AttachmentSlotProps) {
  const [type, setType] = useState<AttachmentType>('craneLiftPlan');
  const [docNumber, setDocNumber] = useState('');
  const [company, setCompany] = useState('');
  const [planDate, setPlanDate] = useState('');
  const [revision, setRevision] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const metaForUpload = (): Record<string, unknown> | null => {
    const raw: Record<string, unknown> = { docNumber };
    if (company.trim()) raw.company = company;
    if (planDate.trim()) raw.planDate = planDate;
    if (revision.trim()) raw.revision = revision;
    const result = validateAttachmentMetadata(raw);
    return result.ok ? (result.cleaned as Record<string, unknown>) : null;
  };

  // Download is token-auth, so fetch the blob through the api client (which
  // carries the bearer token) and save it from the object URL — a bare href
  // would 401 / dump HTML into the tab.
  const downloadAttachment = async (name: string) => {
    if (!planId) return;
    try {
      const res = await api.get(
        `/construction-plans/${planId}/attachments/${encodeURIComponent(name)}`,
        { responseType: 'blob' }
      );
      const blob: Blob = res instanceof Blob ? res : ((res as { data?: Blob }).data as Blob);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      onError?.(planApiErrorMessage(error, 'Attachment download failed — try again.'));
    }
  };

  const handleUpload = async (file: File | null) => {
    if (!file) return;
    if (!planId) {
      onError?.('Save the construction plan before attaching documents.');
      return;
    }
    if (!acceptsFile(file)) {
      onError?.('Only PDF and image files are accepted.');
      return;
    }
    const metadata = metaForUpload();
    if (!metadata) {
      onError?.('A document number is required before uploading.');
      return;
    }
    setBusy(true);
    try {
      const saved = await uploadAttachment(planId, file, type, metadata);
      const next = (saved.planData?.attachments ?? []) as PlanAttachment[];
      onChange(next);
      setDocNumber('');
      setCompany('');
      setPlanDate('');
      setRevision('');
      if (inputRef.current) inputRef.current.value = '';
    } catch (error) {
      onError?.(planApiErrorMessage(error, 'Attachment upload failed — try again.'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (attachment: PlanAttachment) => {
    if (!planId) return;
    setBusy(true);
    try {
      const saved = await deleteAttachment(planId, attachment.name);
      const next = (saved.planData?.attachments ?? []) as PlanAttachment[];
      onChange(next);
    } catch (error) {
      onError?.(planApiErrorMessage(error, 'Attachment delete failed — try again.'));
    } finally {
      setBusy(false);
    }
  };

  const metaText = (a: PlanAttachment): string => {
    const meta = a.metadata ?? {};
    const bits: string[] = [];
    if (meta.company) bits.push(meta.company);
    if (meta.docNumber) bits.push(`doc ${meta.docNumber}`);
    if (meta.planDate) bits.push(meta.planDate);
    if (meta.revision) bits.push(`rev ${meta.revision}`);
    return bits.join(' · ');
  };

  return (
    <div data-testid="attachment-slot" className="bg-white rounded-lg shadow p-6">
      <h2 className="font-bold text-gray-900 border-b pb-2 mb-4">Supporting Documents</h2>

      {canEdit && (
        <div className="mb-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as AttachmentType)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
                data-testid="attachment-type"
              >
                {ATTACHMENT_SLOT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {attachmentTypeLabel(t)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Document Number <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={docNumber}
                onChange={(e) => setDocNumber(e.target.value)}
                placeholder="e.g. CP-2026-041"
                className="w-full px-3 py-2 border border-gray-300 rounded"
                data-testid="attachment-doc-number"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {type === 'craneLiftPlan' ? 'Crane Company' : 'Issuing Firm'}
              </label>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan Date</label>
                <input
                  type="date"
                  value={planDate}
                  onChange={(e) => setPlanDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Revision</label>
                <input
                  type="text"
                  value={revision}
                  onChange={(e) => setRevision(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 disabled:opacity-50">
            <Upload className="h-4 w-4" />
            {busy ? 'Uploading…' : 'Upload Document (PDF/image, 20MB max)'}
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,image/*"
              className="hidden"
              disabled={busy || !planId}
              onChange={(e) => handleUpload(e.target.files?.[0] ?? null)}
              data-testid="attachment-file-input"
            />
          </label>
          {!planId && (
            <p className="text-xs text-amber-700">
              Save the plan once before uploading attachments.
            </p>
          )}
        </div>
      )}

      {attachments.filter(isSlotManaged).length === 0 ? (
        <p className="text-sm text-gray-500">No supporting documents attached.</p>
      ) : (
        <ul className="divide-y divide-gray-200">
          {attachments.filter(isSlotManaged).map((a) => (
            <li key={a.url || a.name} className="py-3 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-500 shrink-0" />
                  <button
                    onClick={() => downloadAttachment(a.name)}
                    className="text-sm font-medium text-blue-700 hover:underline truncate"
                  >
                    {a.name}
                  </button>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full whitespace-nowrap">
                    {attachmentTypeLabel(a.type)}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  {metaText(a) || 'No metadata'}
                  {' · '}
                  Uploaded by {a.uploadedBy} at {a.uploadedAt}
                </p>
              </div>
              {canEdit && (
                <button
                  onClick={() => handleDelete(a)}
                  disabled={busy}
                  className="text-red-600 hover:text-red-800 disabled:opacity-50 flex items-center gap-1 text-sm whitespace-nowrap"
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
